import { atom } from "jotai";
import { EPHEMERAL_NODE_TYPES } from "@zapier/canvas-constants";
import {
  isDraftNode,
  determineAssetType,
  getAssetId,
  isTemplateNode,
} from "../utils";
import { DefaultNodeData, EphemeralNodeType, NodeType } from "../types";
import { canvasIdAtom } from "../canvas";
import { nodesAtom } from "./nodesAtom";

/**
 * To determine whether you're in draft or build mode:
 *
 * Global checks:
 *   - canvasIdAtom: AI and template pages will have no canvas id
 *   - isDraftModeExcludingTemplatesAtom: true if you're on the AI page or blank canvas using AI Co-pilot.
 *   - isDraftModeIncludingTemplatesAtom: true (like ⬆️) but includes template page
 *
 * Node-level checks:
 *   - isDraftOrTemplateNodeAtomFamily: true if node is draft or in a template (use this when you want to target AI stuff AND template nodes)
 *   - isDraftNotTemplateNodeAtomFamily: true if node is draft but NOT a template node (use this when you want to target AI stuff but NOT template nodes)
 */
export const isDraftModeIncludingTemplatesAtom = atom((get) => {
  const canvasId = get(canvasIdAtom);

  if (!canvasId) {
    return true;
  }

  const nodes = get(nodesAtom);
  return nodes.some((node) => {
    // skip nodes we never persist
    const isNotEphemeral = !EPHEMERAL_NODE_TYPES.includes(
      node.type as EphemeralNodeType,
    );
    const assetType = determineAssetType(
      (node.data as DefaultNodeData)?.services,
      node.type as NodeType,
    );
    const assetId = getAssetId(node.data, assetType);
    const isDraft =
      isDraftNode({ assetId, assetType }) || isTemplateNode(node.id);

    return isNotEphemeral && isDraft;
  });
});

isDraftModeIncludingTemplatesAtom.debugLabel =
  "isDraftModeIncludingTemplatesAtom";
